/**
 * Review Firestore Converter (Client SDK)
 *
 * Client-side converter for use with firebase/firestore (NOT firebase-admin).
 * Converts between Review TypeScript type and Firestore data.
 *
 * Based on strategyConverter pattern from firebase-core module.
 *
 * CRITICAL: Uses Client SDK signature fromFirestore(snapshot, options)
 * NOT Admin SDK signature fromFirestore(data)
 */

import { FirestoreDataConverter, QueryDocumentSnapshot, SnapshotOptions, Timestamp, WithFieldValue } from 'firebase/firestore';
import type { Review } from '@segmently/schema-models/entities';

export const reviewConverter: FirestoreDataConverter<Review> = {
  /**
   * Convert Review model to Firestore data
   * Called when writing to Firestore (create, update operations)
   *
   * Date Field Mapping:
   * - createdAt (string/Date) → createdAt (Timestamp)
   * - reviewdate (string/Date) → reviewdate (Timestamp)
   */
  toFirestore(review: WithFieldValue<Review>): any {
    const result: any = { ...review };

    // Convert Date/string to Firestore Timestamp for createdAt
    if (typeof result['createdAt'] === 'string') {
      result['createdAt'] = Timestamp.fromDate(new Date(result['createdAt']));
    } else if (result['createdAt'] instanceof Date) {
      result['createdAt'] = Timestamp.fromDate(result['createdAt']);
    }

    // Convert Date/string to Firestore Timestamp for reviewdate
    if (typeof result['reviewdate'] === 'string') {
      result['reviewdate'] = Timestamp.fromDate(new Date(result['reviewdate']));
    } else if (result['reviewdate'] instanceof Date) {
      result['reviewdate'] = Timestamp.fromDate(result['reviewdate']);
    }

    return result;
  },

  /**
   * Convert Firestore data to Review model
   * Called when reading from Firestore (get, query, snapshot operations)
   *
   * CRITICAL: Uses Client SDK signature (snapshot, options) NOT Admin SDK signature (data)
   *
   * Date Field Mapping:
   * - createdAt (Timestamp) → createdAt (ISO string)
   * - reviewdate (Timestamp) → reviewdate (ISO string)
   */
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions
  ): Review {
    // Extract data from snapshot using Client SDK method
    const data = snapshot.data(options);
    const result: any = { ...data };

    // Add document ID to result
    result.id = snapshot.id;

    // Convert Firestore Timestamp to ISO string for createdAt
    if (result['createdAt']?.toDate) {
      result['createdAt'] = result["createdAt"].toDate();
    }

    // Convert Firestore Timestamp to ISO string for reviewdate
    if (result['reviewdate']?.toDate) {
      result['reviewdate'] = result["reviewdate"].toDate();
    }

    return result as Review;
  }
};

export default reviewConverter;
